import React from 'react';
import { WorkflowTask } from '../../services/taskService';
import { STATUS_LABEL } from './TaskWorkflowPanel';
import '../../assets/styles/Workflow.css';

interface AssigneeTreeProps {
  tasks: WorkflowTask[];
  // The project's own assignee (FDCMaster.assignee_id/assignee_name) - shown
  // as a node even before any task has been created explicitly, since
  // that's who the project is actually assigned to.
  projectAssigneeId?: number | null;
  projectAssigneeName?: string | null;
  // Who created the project (FDCMaster.user_id/creator name) - shown as the
  // tree's top node, with the assignee nested under it, so the tree reads as
  // "who assigned this to whom" rather than just "who has it now".
  creatorId?: number | null;
  creatorName?: string | null;
}

const initialOf = (name: string | null) => (name ? name.trim().charAt(0).toUpperCase() : '?');

const AssigneeTree: React.FC<AssigneeTreeProps> = ({
  tasks, projectAssigneeId, projectAssigneeName, creatorId, creatorName,
}) => {
  // No "root" task - the PC Manager (the project's own assignee) is always
  // the permanent top node, whether or not any subtask has been created yet.
  // Every subtask (parent_task_id null or not) is just delegated work,
  // rendered as a child of whichever task it's actually nested under, or
  // directly under the PC Manager when it has no parent.
  const topLevelTasks = tasks.filter((t) => t.parent_task_id === null);
  const childrenOf = (taskId: number) => tasks.filter((t) => t.parent_task_id === taskId);

  const renderNode = (task: WorkflowTask) => {
    const children = childrenOf(task.id);
    return (
      <div className="wf-tree-node" key={task.id}>
        <div className="wf-tree-node-box">
          <div className="wf-tree-avatar">{initialOf(task.assignee_name)}</div>
          <div>
            <div className="wf-tree-name">{task.assignee_name || `User #${task.assignee_id}`}</div>
            <div className="wf-tree-task">
              {task.kind === 'qa' ? '🔍 ' : task.kind === 'deliver' ? '📦 ' : task.kind === 'other' ? '📌 ' : ''}
              {task.title || (task.kind === 'deliver' ? 'Deliver to customer' : task.kind === 'qa' ? 'QA review' : task.kind === 'other' ? 'Other subtask' : 'Build subtask')}
              <span className={`wf-status-chip wf-status-${task.status}`}>{STATUS_LABEL[task.status]}</span>
            </div>
          </div>
        </div>
        {children.length > 0 && (
          <div className="wf-tree-children">
            {children.map((child) => renderNode(child))}
          </div>
        )}
      </div>
    );
  };

  const pcManagerNode = projectAssigneeId ? (
    <div className="wf-tree-node">
      <div className="wf-tree-node-box">
        <div className="wf-tree-avatar">{initialOf(projectAssigneeName || null)}</div>
        <div>
          <div className="wf-tree-name">
            {projectAssigneeName || `User #${projectAssigneeId}`}
            <span className="wf-tree-role"> · PC Manager</span>
          </div>
          <div className="wf-tree-task">{topLevelTasks.length === 0 ? 'No subtasks created yet' : `${topLevelTasks.length} subtask${topLevelTasks.length === 1 ? '' : 's'} delegated`}</div>
        </div>
      </div>
      {topLevelTasks.length > 0 && (
        <div className="wf-tree-children">
          {topLevelTasks.map((t) => renderNode(t))}
        </div>
      )}
    </div>
  ) : null;

  if (!creatorId) {
    if (!pcManagerNode) {
      return <div className="wf-empty-hint">No assignee tree yet.</div>;
    }
    return <div className="wf-tree">{pcManagerNode}</div>;
  }

  return (
    <div className="wf-tree">
      <div className="wf-tree-node">
        <div className="wf-tree-node-box">
          <div className="wf-tree-avatar">{initialOf(creatorName || null)}</div>
          <div>
            <div className="wf-tree-name">
              {creatorName || `User #${creatorId}`}
              <span className="wf-tree-role"> · Created by</span>
            </div>
          </div>
        </div>
        {pcManagerNode && <div className="wf-tree-children">{pcManagerNode}</div>}
      </div>
    </div>
  );
};

export default AssigneeTree;
